import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Mail, RefreshCw } from "lucide-react";
import { getAttribution } from "@/lib/useVisitTracker";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [resending, setResending] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!acceptTerms) {
      setErr(t("register.mustAcceptTerms"));
      return;
    }
    setBusy(true);
    try {
      const attr = getAttribution() || {};
      await register(email, password, name, acceptTerms, attr);
      setSent(true);
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const resendLink = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success(t("register.resent"));
    } catch (_ex) {
      toast.error(t("register.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  if (sent) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="w-full max-w-md animate-fade-in">
          <div className="mb-10 text-center">
            <div className="font-display text-5xl leading-none text-stone-900">progres.</div>
          </div>
          <div className="card-soft p-8 text-center" data-testid="register-sent">
            <div className="w-14 h-14 rounded-2xl bg-stone-900 mx-auto flex items-center justify-center mb-5">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h1 className="font-display text-2xl text-stone-900 mb-2 leading-tight">
              {t("register.checkInbox")}
            </h1>
            <p className="text-sm text-stone-500 mb-1">
              {t("register.linkSentTo")}
            </p>
            <p className="text-sm font-semibold text-stone-900 mb-6 break-all">
              {email}
            </p>
            <p className="text-xs text-stone-500 leading-relaxed mb-6">
              <Trans
                i18nKey="register.activatePrompt"
                components={{ b: <span className="font-semibold text-stone-700" /> }}
              />
            </p>
            <button
              onClick={resendLink}
              disabled={resending}
              data-testid="resend-link-btn"
              className="text-xs text-stone-500 hover:text-stone-900 font-semibold flex items-center gap-1.5 mx-auto disabled:opacity-50"
            >
              <RefreshCw className={`w-3 h-3 ${resending ? "animate-spin" : ""}`} />
              {t("register.resend")}
            </button>
            <div className="mt-8 pt-6 border-t border-stone-100 text-center text-sm text-stone-500">
              <Link
                to="/login"
                data-testid="link-login-from-sent"
                className="text-stone-900 font-semibold hover:underline underline-offset-4"
              >
                {t("auth.backToLogin")}
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md animate-fade-in">
        <div className="mb-10 text-center">
          <div className="font-display text-5xl leading-none text-stone-900">progres.</div>
          <div className="text-sm text-stone-500 mt-3">{t("register.startTagline")}</div>
        </div>

        <div className="card-soft p-8">
          <h1 className="font-display text-3xl text-stone-900 mb-2 leading-tight">
            {t("register.title")}
          </h1>
          <p className="text-sm text-stone-500 mb-2">{t("register.shortIntro")}</p>
          <p className="text-xs text-stone-500 mb-8 bg-stone-100 rounded-xl px-3 py-2 border border-stone-200">
            <Trans
              i18nKey="register.trialInfo"
              components={{ b: <span className="font-semibold text-stone-700" /> }}
            />
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">
                {t("auth.name")} <span className="text-stone-500 font-normal">({t("register.optional")})</span>
              </label>
              <input
                data-testid="register-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-stone-50 border border-stone-200 text-stone-900 rounded-2xl px-4 py-3 text-sm focus:outline-none"
                placeholder={t("register.namePlaceholder")}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">{t("auth.email")}</label>
              <input
                data-testid="register-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-stone-50 border border-stone-200 text-stone-900 rounded-2xl px-4 py-3 text-sm focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-stone-700 mb-2">
                {t("auth.password")} <span className="text-stone-500 font-normal">({t("register.pwdMin")})</span>
              </label>
              <input
                data-testid="register-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-stone-50 border border-stone-200 text-stone-900 rounded-2xl px-4 py-3 text-sm focus:outline-none"
                placeholder="••••••••"
              />
            </div>

            {err && (
              <div
                data-testid="register-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3"
              >
                {err}
              </div>
            )}

            <label className="flex items-start gap-3 cursor-pointer group" data-testid="accept-terms-label">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                data-testid="accept-terms"
                className="mt-0.5 w-4 h-4 accent-stone-900 flex-shrink-0"
              />
              <span className="text-xs text-stone-600 leading-relaxed">
                <Trans
                  i18nKey="register.acceptTerms"
                  components={{
                    terms: <Link to="/regulamin" target="_blank" rel="noopener" className="text-stone-900 font-semibold underline underline-offset-2" />,
                    privacy: <Link to="/polityka-prywatnosci" target="_blank" rel="noopener" className="text-stone-900 font-semibold underline underline-offset-2" />,
                    dpa: <Link to="/umowa-powierzenia" target="_blank" rel="noopener" className="text-stone-900 font-semibold underline underline-offset-2" />,
                  }}
                />
              </span>
            </label>

            <button
              data-testid="register-submit"
              type="submit"
              disabled={busy || !acceptTerms}
              className="btn-pill w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? t("register.creating") : (<>{t("register.createBtn")} <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-stone-100 text-center text-sm text-stone-500">
            {t("auth.haveAccount")}{" "}
            <Link
              data-testid="link-login"
              to="/login"
              className="text-stone-900 font-semibold hover:underline underline-offset-4 ml-1"
            >
              {t("auth.loginBtn")}
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
